/**
 * Slide track for `sections/mmx-about-slider.liquid`.
 *
 * Slides sit side by side in a flex track that is translated one slide at a
 * time, so on desktop the next slide's photo peeks past the content column the
 * way the artboard draws it. Slides behind the active one fade out so nothing
 * shows in the left page margin. The dots are built here from the slides that
 * are actually in the DOM; the section renders the controls hidden when there
 * is only one slide, and this removes that attribute once there is something
 * to drive.
 */
class MmxAboutSlider extends HTMLElement {
  /** @type {number} */
  #index = 0;

  /**
   * Listeners are bound per connection: a section re-render in the theme editor
   * moves the element rather than replacing it, so `connectedCallback` runs again
   * and un-scoped listeners would stack.
   * @type {AbortController}
   */
  #listeners = new AbortController();

  connectedCallback() {
    this.#listeners = new AbortController();

    this.viewport = this.querySelector('[data-mmx-as-viewport]');
    this.track = this.querySelector('[data-mmx-as-track]');
    this.slides = Array.from(this.querySelectorAll('[data-mmx-as-slide]'));
    this.dotsHost = this.querySelector('[data-mmx-as-dots]');
    this.prev = this.querySelector('[data-mmx-as-prev]');
    this.next = this.querySelector('[data-mmx-as-next]');
    this.controls = this.querySelector('.mmx-as__controls');

    if (this.slides.length <= 1) {
      this.controls?.setAttribute('hidden', '');
      this.slides[0]?.classList.add('is-active');
      return;
    }

    this.controls?.removeAttribute('hidden');
    this.#buildDots();
    this.#bind();
    this.#go(0, false);

    // Copy reflows once the web font arrives; re-measure the active slide then.
    document.fonts?.ready.then(() => this.#go(this.#index, false));
  }

  disconnectedCallback() {
    this.#listeners.abort();
  }

  #buildDots() {
    if (!this.dotsHost) return;

    this.dotsHost.textContent = '';
    this.dots = this.slides.map((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'mmx-as__dot';
      dot.setAttribute('aria-label', `${index + 1}`);
      this.dotsHost?.append(dot);
      return dot;
    });
  }

  #bind() {
    const { signal } = this.#listeners;

    this.prev?.addEventListener('click', () => this.#go(this.#index - 1), { signal });
    this.next?.addEventListener('click', () => this.#go(this.#index + 1), { signal });
    this.dots?.forEach((dot, index) => {
      dot.addEventListener('click', () => this.#go(index), { signal });
    });

    // The translate is measured in px, so re-measure whenever the column
    // changes width.
    let raf = 0;
    window.addEventListener(
      'resize',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.#go(this.#index, false));
      },
      { signal }
    );

    // Theme editor: jump to the block the merchant selects.
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const index = this.slides.findIndex((slide) => slide.contains(event.target));
        if (index >= 0) this.#go(index);
      },
      { signal }
    );

    // Horizontal drag on the track, for touch. Vertical intent is left alone so
    // the swipe never fights page scrolling.
    let startX = null;
    let startY = null;

    this.track?.addEventListener(
      'pointerdown',
      (event) => {
        startX = event.clientX;
        startY = event.clientY;
      },
      { signal }
    );

    this.track?.addEventListener(
      'pointerup',
      (event) => {
        if (startX == null || startY == null) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        startX = null;
        startY = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          this.#go(this.#index + (dx < 0 ? 1 : -1));
        }
      },
      { signal }
    );
  }

  /**
   * @param {number} next
   * @param {boolean} [animate]
   */
  #go(next, animate = true) {
    const count = this.slides.length;
    if (count === 0 || !this.track) return;

    this.#index = ((next % count) + count) % count;

    const active = this.slides[this.#index];
    const offset = active.offsetLeft - this.slides[0].offsetLeft;
    this.track.classList.toggle('is-static', !animate);
    this.track.style.transform = `translateX(${-offset}px)`;

    // Cards grow with their copy, so the track is as tall as the slide on
    // screen — not the tallest slide — and the controls sit right under it.
    if (this.viewport) {
      this.viewport.style.height = `${active.offsetHeight}px`;
    }

    this.slides.forEach((slide, index) => {
      const active = index === this.#index;
      slide.classList.toggle('is-active', active);
      slide.classList.toggle('is-past', index < this.#index);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.dots?.forEach((dot, index) => {
      const active = index === this.#index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }
}

if (!customElements.get('mmx-about-slider')) {
  customElements.define('mmx-about-slider', MmxAboutSlider);
}
